import _ from "lodash";
import { Form } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { Button2 } from "@/ui2/atoms/Button";
import { CustomForm } from "@/component-library";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import { Divider2 } from "@/ui2/atoms/Divider";
import { filterTimePickerOptionsByLabel } from "@/features/calendar/utils/dates";
import { FormDateField } from "@/component-library/FormFields/FormDateField/FormDateField";
import { FormMultiSelect } from "@/component-library/FormFields/FormMultiSelect/FormMultiSelect";
import { FormRadioGroup } from "@/component-library/FormFields/FormRadioGroup/FormRadioGroup";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { providerRequests } from "@/api/(requests)/provider";
import { ScrollToFormError } from "@/component-library/ScrollToFormError/ScrollToError";
import { testLocator } from "@/lib/helpers/test-locator";

import {
  ACTIVITY_CLASS_OPTIONS,
  ACTIVITY_PURPOSE_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
  ACTIVITY_SEVICE_TYPE_OPTIONS,
  ACP_NOTE_FIELDS_CAST_TO_BOOLEAN,
  CARE_TEAM_MEMBER_TYPE_OPTIONS,
  COMMUNICATION_TYPE_OPTIONS,
  CONDITIONAL_FIELD_CLEARING_RULES,
  INTENDED_DURATION_OPTIONS,
  REASON_FOR_COMMUNICATION_OPTIONS,
  RECOMMENDED_NEXT_OPTIONS,
  TIME_SLOT_OPTIONS,
  YES_NO_OPTIONS,
  filterDurationOptionsByLabel,
} from "../constants";
import { awaitNotesSummaryRefresh } from "../../helpers/awaitNotesSummaryRefresh";
import { CredentialField } from "../../components/CredentialField";
import { ProviderSignatureField } from "../../components/ProviderSignatureField";

import { ADVANCED_CARE_ACTIVITY_SCHEMA } from "./AdvancedCareActivity.schema";
import { AdvancedCareActivityTextArea } from "./AdvancedCareActivityTextArea";
import { AdvancedCareActivityTextInput } from "./AdvancedCareActivityInput";

export function AdvancedCareActivityForm({
  isEditing,
  isEmbedded = false,
  nameToSign,
  noteState,
  patientId,
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const preventEnterFormSubmission = (event) => {
    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
    }
  };

  const renderForm = (formikProps) => {
    return (
      <Form
        className="tw:grid! tw:items-start tw:gap-8 tw:max-lg:grid-cols-1 tw:lg:grid-cols-2 tw:print:grid-cols-2!"
        onKeyDown={preventEnterFormSubmission}
      >
        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          formikProps={formikProps}
          name="content.activity_service_type"
          options={ACTIVITY_SEVICE_TYPE_OPTIONS}
          placeholder="Choose most relevant"
          testId="advancedCareActivityActivityServiceTypeField"
          title="Advanced Care Service"
          triggerErrorOnChange
        />

        <div className="tw:flex tw:flex-col tw:gap-5">
          <FormMultiSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.activity_purpose"
            options={ACTIVITY_PURPOSE_OPTIONS}
            placeholder="Search Activity Type"
            testId="advancedCareActivityActivityPurposeField"
            title="Activity Type"
            triggerErrorOnChange
          />

          {formikProps.values.content.activity_purpose.includes("Other") && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_purpose_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityActivityPurposeOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterDurationOptionsByLabel}
          formikProps={formikProps}
          name="content.intended_duration"
          options={INTENDED_DURATION_OPTIONS}
          placeholder="Anticipated duration of the activity"
          testId="advancedCareActivityDurationField"
          title="Intended Duration"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="service_location"
          options={ACTIVITY_CLASS_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityLocationField"
          title="Activity Location"
          triggerErrorOnChange
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.communication_type"
            options={COMMUNICATION_TYPE_OPTIONS}
            placeholder="Search Communication Method"
            testId="advancedCareActivityCommunicationTypeField"
            title="Communication Method"
            triggerErrorOnChange
          />

          {formikProps.values.content.communication_type === "Other" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.communication_type_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityCommunicationTypeOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.activity_reason"
          options={ACTIVITY_REASON_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityActivityReasonField"
          title="Was the activity planned?"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.patient_encounter"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityEncounterField"
          title="Was the patient expected to be present?"
          triggerErrorOnChange
        />

        <FormRadioGroup
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.patient_participant_attest"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityAttendField"
          title="Did the patient attend?"
          triggerErrorOnChange
        />

        <FormDateField
          disabled={!isEditing}
          formikProps={formikProps}
          inputClassName={!isEditing && "AdvancedCareActivityForm-input_read_only"}
          name="session_date"
          placeholder="MM/DD/YYYY"
          testId="advancedCareActivityDateField"
          title="Activity Date"
          triggerErrorOnChange
          {...getAutomationDataAttr("advanced-care-activity-assessment-date-field")}
        />

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          name="session_from"
          options={TIME_SLOT_OPTIONS}
          otherErrorNames={["time_error", "start_date_time_error"]}
          placeholder="HH:MM"
          testId="advancedCareActivityStartField"
          title="Start Time"
          triggerErrorOnChange
        />

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          name="session_to"
          options={TIME_SLOT_OPTIONS}
          otherErrorNames={["time_error", "end_date_time_error"]}
          placeholder="HH:MM"
          testId="advancedCareActivityEndField"
          title="End Time"
          triggerErrorOnChange
        />

        <FormMultiSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          formikProps={formikProps}
          name="content.reason_for_communication"
          options={REASON_FOR_COMMUNICATION_OPTIONS}
          placeholder="Search Reason for Communication"
          testId="advancedCareActivityReasonForCommunicationField"
          title="Reason for Communication"
          triggerErrorOnChange
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormRadioGroup
            className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            disabled={!isEditing}
            formikProps={formikProps}
            name="content.activity_result"
            options={YES_NO_OPTIONS}
            radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
            radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
            testId="advancedCareActivityResultField"
            title="Was the activity completed as planned?"
            triggerErrorOnChange
          />

          {formikProps.values.content.activity_result === "No" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_result_note"
              readOnly={!isEditing}
              testId="advancedCareActivityResultNoteField"
              title="Why was the activity not completed as planned?"
              triggerErrorOnChange
            />
          )}
        </div>

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormMultiSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.recommended_next"
            options={RECOMMENDED_NEXT_OPTIONS}
            placeholder="Search Care team follow up"
            testId="advancedCareActivityRecommendedNextField"
            title="Care Team Follow Up"
            triggerErrorOnChange
          />

          {formikProps.values.content.recommended_next.includes("Other") && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.recommended_next_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityRecommendedNextOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        <Divider2 className="tw:lg:col-span-2 tw:print:col-span-2" />

        {/* ACUTE EPISODE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.acute_episode"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityAcuteEpisode"
          title="ED visit or inpatient care for BH within 6 months?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.acute_episode ||
            formikProps.values.content.acute_episode === "No"
          }
          name="content.acute_episode_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          testId="advancedCareActivityAcuteEpisodeDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* ACTIVE SUBSTANCE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.active_substance"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityActiveSubstance"
          title="Active substance use disorder?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.active_substance ||
            formikProps.values.content.active_substance === "No"
          }
          name="content.active_substance_details"
          placeholder="Include type of substance and frequency if comfortable sharing"
          readOnly={!isEditing}
          testId="advancedCareActivityActiveSubstanceDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* SDOH */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.sdoh"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivitySdoh"
          title="Housing, Financial, or Safety Concerns (SDOH)?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={!formikProps.values.content.sdoh || formikProps.values.content.sdoh === "No"}
          name="content.sdoh_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          testId="advancedCareActivitySdohDetails"
          title="Details"
          triggerErrorOnChange
        />

        {/* DAILY ACTIVITIES */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.activity_difficulties"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advancedCareActivityDailyActivities"
          title="Difficulty with Activities of Daily Living (ADLs)?"
          triggerErrorOnChange
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.activity_difficulties ||
            formikProps.values.content.activity_difficulties === "No"
          }
          name="content.activity_difficulties_details"
          placeholder="Please provide details (cleaning, cooking, toileting, etc.)"
          readOnly={!isEditing}
          testId="advancedCareActivityDailyActivitiesDetails"
          title="Details"
          triggerErrorOnChange
        />

        <Divider2 className="tw:lg:col-span-2 tw:print:col-span-2" />

        {/* SUMMARY */}
        <div className="tw:lg:col-span-2 tw:print:col-span-2">
          <AdvancedCareActivityTextArea
            className="AdvancedCareActivityForm-summary_form_field"
            formikProps={formikProps}
            inputClassName="AdvancedCareActivityForm-summary_form_field_input"
            name="content.additional_information"
            placeholder="Highlight interventions and patient reactions"
            readOnly={!isEditing}
            testId="advancedCareActivitySummaryField"
            title="Summary"
            triggerErrorOnChange
          />
        </div>

        {/* YOUR ROLE */}
        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.care_team_member_type"
            options={CARE_TEAM_MEMBER_TYPE_OPTIONS}
            placeholder="Search Role"
            testId="advancedCareActivityRoleField"
            title="Your Role"
            triggerErrorOnChange
          />

          {formikProps.values.content.care_team_member_type === "Other" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.care_team_member_type_other_details"
              readOnly={!isEditing}
              testId="advancedCareActivityRoleOtherField"
              title="Specify Other"
              triggerErrorOnChange
            />
          )}
        </div>

        {/* SIGNATURE/DATE/CREDENTIALS */}
        <div className="tw:col-start-1 tw:lg:col-span-2 tw:lg:flex tw:lg:justify-between tw:print:col-span-2">
          {isEditing && (
            <ProviderSignatureField
              formikProps={formikProps}
              isViewOnly={false}
              name="signature"
              nameToSign={nameToSign}
              testId="advancedCareActivitySignature"
            />
          )}

          {/* CREDENTIALS */}
          {isEditing && (
            <>
              {formikProps.values["provider_cp_credentials"] && (
                <CredentialField
                  formikProps={formikProps}
                  isViewOnly={false}
                  name="provider_cp_credentials"
                />
              )}
            </>
          )}

          <ScrollToFormError />
        </div>

        {/* SUBMIT */}
        {isEditing && (
          <Button2
            className="tw:min-w-50 tw:lg:col-start-2 tw:lg:ml-auto!"
            size="md"
            type="submit"
            {...getAutomationDataAttr("advanced-care-activity-assessment-submit-button")}
            {...testLocator("advancedCareActivitySubmitBtn")}
          >
            Sign & Submit
          </Button2>
        )}
      </Form>
    );
  };

  const onSubmit = async (formData) => {
    const copyOfFormData = _.cloneDeep(formData);
    const { content } = copyOfFormData;

    // Clear conditional details fields based on their parent field values
    CONDITIONAL_FIELD_CLEARING_RULES.forEach(({ detailsField, shouldClear }) => {
      if (shouldClear(content)) {
        copyOfFormData.content[detailsField] = "";
      }
    });

    ACP_NOTE_FIELDS_CAST_TO_BOOLEAN.forEach((item) => {
      const foundOption = YES_NO_OPTIONS.find(
        (option) => option.value === copyOfFormData.content[item],
      );

      copyOfFormData.content[item] = foundOption.booleanValue;
    });

    copyOfFormData["session_date"] = dayjs(copyOfFormData["session_date"], "MMDDYYYY").format(
      "MM/DD/YYYY",
    );

    copyOfFormData["content"]["intended_duration"] = Number(
      copyOfFormData["content"]["intended_duration"],
    );

    copyOfFormData["completed_date_time"] = dayjs().format();

    const urlParams = { patientId };

    await providerRequests.post_progress_note({ data: copyOfFormData, urlParams });
    await awaitNotesSummaryRefresh(patientId, dispatch);
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  if (isEmbedded) {
    return noteState ? (
      <CustomForm
        initialValues={noteState}
        onSubmit={onSubmit}
        render={renderForm}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={ADVANCED_CARE_ACTIVITY_SCHEMA}
      />
    ) : null;
  }

  return (
    <DashboardCard2
      rawChildren
      title="Advanced Care Activity Assessment"
      {...getAutomationDataAttr("advanced-care-activity-assessment-title")}
    >
      {noteState && (
        <CustomForm
          initialValues={noteState}
          onSubmit={onSubmit}
          render={renderForm}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={ADVANCED_CARE_ACTIVITY_SCHEMA}
        />
      )}
    </DashboardCard2>
  );
}
